// src/components/layout/Sidebar.tsx
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  FileText,
  HeadphonesIcon,
  HelpCircle,
  Home,
  Image,
  Layout,
  LogOut,
  Settings,
  Star,
  User,
  Database,
  Library,
  Files,
  FolderPlus,
  Menu,
  X,
  IndianRupee
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePageContext } from '@/contexts/PageContext';
import { usePermissionCheck } from '@/hooks/useGlobalPermission';
import { NavItem } from '@/types';
import { PERMISSIONS_LIST } from '@/utils/roles';
import { PermissionWrapper } from '@/components/PermissionWrapper';
import { Tooltip } from '@/components/common/Tooltip';
import { useProjects } from '@/components/create-project/hooks/useProjects';
import { toast } from 'react-hot-toast';
import { ProjectListModal } from '@/components/create-project/ProjectListModal';
import { Project } from '@/components/create-project/types/project';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const { setCurrentPage, collapsed, setCollapsed, templates, isPageAccessible } = usePageContext();
  const { doLogout, user } = useAuth();
  const { checkPermission } = usePermissionCheck();
  const { selectedProject, selectProject } = useProjects();
  const [isProjectModalOpen, setProjectModalOpen] = useState(false);
  const canManageProjects = checkPermission(PERMISSIONS_LIST.VIEW_SETTINGS);

  // Use a mapping for refs based on unique paths
  const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});

  // Filter templates for quick access
  const singlePageTemplates = templates.filter((template) => template.isSinglePage && template.quickAccess);
  const multiPageTemplates = templates.filter((template) => !template.isSinglePage && template.quickAccess);

  const handleProjectSelect = useCallback(
    async (project: Project) => {
      await selectProject(project);
      setProjectModalOpen(false);
    },
    [selectProject]
  );

  const menuItems: NavItem[] = [
    { path: '/', label: 'Dashboard', icon: Home, permission: PERMISSIONS_LIST.VIEW_DASHBOARD },
    { path: '/content-types', label: 'Content Types', icon: Edit, permission: PERMISSIONS_LIST.VIEW_TEMPLATES },
    { path: '/template-builder', label: 'Template Builder', icon: Layout, permission: PERMISSIONS_LIST.VIEW_TEMPLATES },
    { path: '/asset-manager', label: 'Asset Manager', icon: Image, permission: PERMISSIONS_LIST.VIEW_ASSETS },
    { path: '/content-library', label: 'Content Library', icon: Library, permission: PERMISSIONS_LIST.VIEW_LIBRARY },
    { path: '/attribute-sets', label: 'Attribute Sets', icon: Database, permission: PERMISSIONS_LIST.VIEW_ATTRIBUTES },
    { path: '/profile', label: 'Profile', icon: User, permission: PERMISSIONS_LIST.VIEW_PROFILE },
    { path: '/roles', label: 'Roles', icon: User, permission: PERMISSIONS_LIST.VIEW_ROLES },
    { path: '/settings', label: 'Settings', icon: Settings, permission: PERMISSIONS_LIST.VIEW_SETTINGS },
    { path: '/billing', label: 'Billing', icon: IndianRupee, permission: PERMISSIONS_LIST.VIEW_ASSETS }, // TO-DO :- need to change VIEW_BILLING
  ];

  const utilityItems: NavItem[] = [
    { path: '/help', label: 'Help', icon: HelpCircle, permission: PERMISSIONS_LIST.VIEW_HELP_GUIDE },
    { path: '/support', label: 'Support', icon: HeadphonesIcon, permission: PERMISSIONS_LIST.VIEW_SUPPORT },
    { path: '#', label: 'Logout', icon: LogOut, onClick: doLogout },
  ];

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const renderNavItems = useCallback(
    (items: NavItem[]) =>
      items.map((item) => {
        // Check both permissions and page accessibility
        if (item.permission && !checkPermission(item.permission)) {
          return null;
        }
        
        const isAccessible = isPageAccessible(item.path);

        // Assign ref based on item.path
        const setRef = (el: HTMLAnchorElement | null) => {
          itemRefs.current[item.path] = el;
        };

        return (
          <div key={item.path} className="relative group">
            <Link
              ref={setRef}
              to={item.path}
              className={`flex items-center px-4 py-3 transition duration-200 font-medium 
                ${isActive(item.path)
                  ? 'bg-primary-100 text-primary-950 border-r-2 border-primary-950'
                  : ' hover:bg-primary-100'
                }
                ${!isAccessible ? 'opacity-50' : ''}`}
              onClick={(e) => {
                if (!isAccessible) {
                  e.preventDefault();
                  toast.error(`Please select a project first ${item.path}`);
                  return;
                }
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                } else {
                  setCurrentPage(item.label);
                }
              }}
            >
              <item.icon className={`h-4 w-4 text-gray-600 ${isActive(item.path) ? 'text-primary-500' : ''} ${collapsed ? 'mx-auto' : 'mr-4'}`} />
              {!collapsed && <span className="truncate text-sm">{item.label}</span>}
            </Link>
            {collapsed && itemRefs.current[item.path] && (
              <Tooltip text={item.label} targetRef={{ current: itemRefs.current[item.path] as HTMLElement }} />
            )}
          </div>
        );
      }),
    [checkPermission, collapsed, setCurrentPage, isActive, isPageAccessible]
  );

  // Handle localStorage persistence
  useEffect(() => {
    // Get initial state from localStorage
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsed !== null) {
      setCollapsed(savedCollapsed === 'true');
    }
  }, [setCollapsed]);

  // Update localStorage when collapsed state changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-full bg-white ${collapsed ? 'w-0 sm:w-16' : 'sm:w-64'
          } transition-all duration-200 z-20`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-20 flex items-center p-4">
            <div className={collapsed ? 'w-full flex justify-center' : 'w-full flex justify-start'}>
              <Link to="/">
                {collapsed ? (
                  <img
                    src="https://d3402d7u0raa48.cloudfront.net/UJTW58TCB/Image/de356639-c0d2-4dea-9602-61e96178dc04.jpg"
                    alt="Hedra Icon"
                    className="h-8 w-8 object-contain"
                  />
                ) : (
                  <img
                    src="https://d3402d7u0raa48.cloudfront.net/UJTW58TCB/Image/27b28d81-ebfc-4732-a4ee-eaa93fc5e315.jpg"
                    alt="Hedra Logo"
                    className="h-10 object-contain"
                  />
                )}
              </Link>
            </div>
          </div>

          {/* Project selector (always available, even without dashboard permission) */}
          {!collapsed && (
            <button
              type="button"
              onClick={() => setProjectModalOpen(true)}
              className="px-4 py-3 bg-green-300 text-left w-full hover:bg-green-200 transition"
            >
              <div className="flex items-start gap-4">
                <FolderPlus className="w-5 h-5 text-primary-950" />
                <div className="flex flex-col">
                  <span className="text-xs text-primary-950 font-medium">Current Project</span>
                  <span className="text-sm font-semibold text-primary-950 truncate">
                    {selectedProject ? selectedProject.name : 'Select a project'}
                  </span>
                  <span className="text-xs text-primary-900 underline mt-1">Switch project</span>
                </div>
              </div>
            </button>
          )}
          {collapsed && (
            <div className="flex items-center justify-center py-3">
              <button
                type="button"
                onClick={() => setProjectModalOpen(true)}
                className="p-2 rounded-md hover:bg-primary-100 transition"
                title={selectedProject ? `Current: ${selectedProject.name}` : 'Select a project'}
              >
                <FolderPlus className="w-5 h-5 text-primary-950" />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className={`flex-1 overflow-x-hidden overflow-y-auto scrollable-element ${collapsed ? 'p-0' : 'p-0'}`}>
            {/* Main Menu with updated spacing */}
            <div className="space-y-2">
              {renderNavItems(menuItems)}
            </div>

            {/* Quick Access Templates with improved headers */}
            <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_CONTENT}>
              {(singlePageTemplates.length > 0 || multiPageTemplates.length > 0) && (
                <div className="space-y-2 mt-3 border-t border-gray-300">
                  <h3 className={`text-sm font-semibold uppercase px-4 pt-4 text-primary-500 rounded-md ${collapsed ? 'hidden' : ''}`}>
                    Quick Access Pages
                  </h3>
                  {singlePageTemplates.length > 0 && (
                    <>
                      <h4 className={`text-xs text-slate-500 uppercase bg-primary-50 py-2  ${collapsed ? 'px-1.5 text-center' : 'px-4 text-left'}`}>
                        {collapsed ? 'Single' : 'Single Page'}
                      </h4>
                      {renderNavItems(
                        singlePageTemplates.map((template) => ({
                          path: `/page-content/${template.templateId}`,
                          label: template.templateName,
                          icon: FileText,
                          permission: PERMISSIONS_LIST.VIEW_CONTENT,
                        }))
                      )}
                    </>
                  )}

                  {multiPageTemplates.length > 0 && (
                    <>
                      <h4 className={`text-xs bg-primary-50 rounded py-2 bg-primary-50 rounded-md ${collapsed ? 'px-1.5 text-center' : 'px-3 text-left'}`}>
                        {collapsed ? 'Multi' : 'Multi Page'}
                      </h4>
                      {renderNavItems(
                        multiPageTemplates.map((template) => ({
                          path: `/page-content/${template.templateId}`,
                          label: template.templateName,
                          icon: Files,
                          permission: PERMISSIONS_LIST.VIEW_CONTENT,
                        }))
                      )}
                    </>
                  )}
                </div>
              )}
            </PermissionWrapper>
          </nav>

          {/* Trial Status - New Section */}
          {/* TODO: Add trial status back in with api implementation */}
          {/* {!collapsed && (
            <div className="border-t border-slate-200 pt-4">
              <div className="px-3 pb-3">
                <div className={`rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 text-white p-4 ${collapsed ? 'text-center' : ''}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4" />
                    <span className={`font-medium ${collapsed ? 'hidden' : ''}`}>Trial ends in 6 days</span>
                  </div>
                  <>
                    <p className="text-sm text-white/90 mb-3">
                      You are on a free Premium Trial plan on monthly billing.
                    </p>
                    <Link
                      to="/subscription/details"
                      className="block w-full py-2 px-4 bg-white text-center rounded-xl text-sm font-medium hover:bg-neutral-50 transition-colors"
                    >
                      View more details
                    </Link>
                  </>
                </div>
              </div>
            </div>
          )} */}

          {/* Update utility items section */}
          <div className="mt-auto border-t border-gray-200">
            <div className={`space-y-0.5 ${collapsed ? 'p-0' : 'p-0'}`}>
              {renderNavItems(utilityItems)}
            </div>
          </div>

          {/* Upgrade Banner */}
          {checkPermission('view-subscription') && !user?.isTeamMember && <div className={`${collapsed ? 'p-2' : 'p-4'}`}>
            <Link
              to="/subscription"
              className={`flex items-center justify-center gap-2 py-2 px-4 bg-highlight-500 text-primary-900 rounded-md font-semibold transition duration-200 hover:bg-highlight-400 text-sm ${collapsed ? 'mx-auto w-12 h-12' : ''}`}
            >
              <Star className={`h-5 w-5`} />
              {!collapsed && <span>Upgrade Plan</span>}
            </Link>
          </div>
          }
        </div>

        {/* Update collapse button styling */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`absolute -right-[52px] sm:-right-3 top-10 sm:top-1/2 transform -translate-y-1/2 p-1.5 bg-white border-0 sm:border border-slate-200 rounded-md sm:rounded-full hover:bg-slate-50 transition-all duration-200 z-30 ${collapsed ? '' : ''}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <>
            <Menu className='block sm:hidden' />
            <ChevronRight className="hidden sm:block h-3.5 w-3.5 text-slate-600" />
            </>
          ) : (
            <>
            <X className='block sm:hidden' />
            <ChevronLeft className="hidden sm:block h-3.5 w-3.5 text-slate-600" />
            </>
          )}
        </button>
      </aside>

      {/* Spacer */}
      {/* <div className={`${collapsed ? 'w-16' : 'w-64'} transition-all duration-200`} /> */}

      <ProjectListModal
        open={isProjectModalOpen}
        onClose={() => setProjectModalOpen(false)}
        onSelectProject={handleProjectSelect}
        canManageProjects={canManageProjects}
      />
    </>
  );
};
